import { useSyncExternalStore } from "react";
import { diag, since } from "./diag";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type UploadTask,
} from "@firebase/storage";
import { doc, onSnapshot } from "@firebase/firestore";
import { auth, db, storage } from "./firebase";
import { getLocalVideoPath, readLocalVideoChunk, releaseLocalVideo } from "./native-media-picker";
import { resumableUpload } from "./resumable-upload";

/**
 * Background video uploads.
 *
 * Videos are uploaded as-is (resumable, with progress) and then compressed to
 * 720p H.264 on the server (see `convertChatVideoToMp4` in functions), which
 * swaps the URL on the post/comment/message and deletes the original.
 *
 * Jobs live at module level, so an upload keeps running while the user
 * navigates around the app. They do NOT survive the app being closed or
 * suspended by the OS, which is why the UI asks users to keep the app open
 * until the upload phase is done.
 */

export const MAX_VIDEO_BYTES = 1024 * 1024 * 1024; // 1 GB (mirrored in storage.rules)
const STALL_TIMEOUT_MS = 60_000;
const READ_CHECK_TIMEOUT_MS = 15_000;
const PROCESSING_WATCH_TIMEOUT_MS = 15 * 60_000;
const DONE_DISMISS_MS = 4_000;

const VIDEO_EXTENSIONS = [".mp4", ".mov", ".m4v", ".webm", ".3gp", ".hevc"];

export type VideoFolder = "posts" | "comments" | "chat-videos";
export type VideoUploadStatus = "uploading" | "processing" | "done" | "error";

export interface VideoUploadJob {
  id: string;
  label: string;
  fileName: string;
  status: VideoUploadStatus;
  progress: number; // 0-100, upload phase only
  error?: string;
}

export interface UploadedVideo {
  url: string;
  storagePath: string;
}

export interface StartVideoUploadOptions {
  /** Short label shown in the progress bar, e.g. "Objava". */
  label: string;
  file: File;
  folder: VideoFolder;
  /** Custom storage metadata; the server uses `refPath` / `groupId` to find the document. */
  metadata?: Record<string, string>;
  /**
   * Called once the file is fully uploaded. Should create the post/comment/message
   * (with `videoProcessing: true` and `videoProcessingPath: storagePath`) and
   * return its Firestore path so the job can follow server-side processing.
   */
  onUploaded: (video: UploadedVideo) => Promise<string | undefined>;
  /** Called when an attempt starts (also on "Ponovi"). */
  onAttempt?: () => void;
  /** Called when an attempt fails (code "storage/canceled" when cancelled). */
  onFailed?: (error: unknown) => void;
}

interface JobEntry {
  job: VideoUploadJob;
  options: StartVideoUploadOptions;
  /** Stops the running upload (rejects with storage/canceled). */
  cancel?: () => void;
  /** Cancel was pressed before the upload itself started (while checking the video). */
  cancelRequested?: boolean;
  /**
   * The finished upload, once it's in storage. If saving the message/post
   * then fails, "Ponovi" only saves again instead of uploading again (the
   * video may no longer be readable from the device by then).
   */
  uploadedVideo?: UploadedVideo;
  cleanup: (() => void)[];
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const entries = new Map<string, JobEntry>();
const listeners = new Set<() => void>();
let snapshot: VideoUploadJob[] = [];

function emit() {
  snapshot = Array.from(entries.values()).map((e) => e.job);
  listeners.forEach((l) => l());
}

function patch(id: string, changes: Partial<VideoUploadJob>) {
  const entry = entries.get(id);
  if (!entry) return;
  entry.job = { ...entry.job, ...changes };
  emit();
}

function runCleanup(entry: JobEntry) {
  entry.cleanup.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
  entry.cleanup = [];
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useVideoUploads(): VideoUploadJob[] {
  return useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
}

// Jobs whose file is uploaded but whose post/comment/message is still being
// created. Reloading then would lose it (the file would be left orphaned).
const saving = new Set<string>();

export function hasActiveVideoUploads(): boolean {
  return saving.size > 0 || snapshot.some((j) => j.status === "uploading");
}

// Warn before closing/reloading the page (web) while an upload is running.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (hasActiveVideoUploads()) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function isVideoFile(file: File): boolean {
  if (file.type.startsWith("video/")) return true;
  const name = file.name.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function readSlice(blob: Blob | Promise<Blob>): Promise<number> {
  return Promise.race([
    Promise.resolve(blob).then((b) => b.arrayBuffer()).then((b) => b.byteLength),
    new Promise<number>((_, reject) =>
      setTimeout(() => reject(new Error("read timeout")), READ_CHECK_TIMEOUT_MS),
    ),
  ]);
}

/**
 * Returns a user-facing error message if the video can't be uploaded, or null if it's fine.
 * On iOS the picked file can be empty or become unreadable, which previously
 * produced 0-byte "broken" videos in the feed.
 */
export async function validateVideoFile(file: File): Promise<string | null> {
  const started = performance.now();
  const result = await checkVideoFile(file);
  diag("video picked", {
    mb: Math.round(file.size / (1024 * 1024)),
    type: file.type || "?",
    checkMs: since(started),
    ok: !result,
  });
  return result;
}

async function checkVideoFile(file: File): Promise<string | null> {
  if (file.size === 0) {
    return "Video je prazan ili se nije mogao učitati. Odaberite ga ponovno.";
  }
  if (file.size > MAX_VIDEO_BYTES) {
    return "Video je prevelik (najviše 1 GB). Skratite ga ili odaberite manji video.";
  }
  try {
    const chunk = 64 * 1024;
    const tailStart = Math.max(0, file.size - chunk);
    // A video from the native gallery picker is read from disk instead.
    const read = getLocalVideoPath(file)
      ? (start: number, end: number) => readSlice(readLocalVideoChunk(file, start, end))
      : (start: number, end: number) => readSlice(file.slice(start, end));
    const head = await read(0, Math.min(chunk, file.size));
    const tail = await read(tailStart, file.size);
    if (head === 0 || tail === 0) throw new Error("empty read");
  } catch {
    return "Video se nije mogao pročitati s uređaja. Odaberite ga ponovno.";
  }
  return null;
}

/**
 * True while the server is still compressing an item's video. Falls back to
 * the original upload after a while so a failed job can't hide it forever.
 */
export function isVideoStillProcessing(item: {
  videoProcessing?: boolean;
  createdAt?: string;
}): boolean {
  if (!item.videoProcessing) return false;
  const created = item.createdAt ? Date.parse(item.createdAt) : NaN;
  return Number.isNaN(created) || Date.now() - created < PROCESSING_WATCH_TIMEOUT_MS;
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

function safeFileName(name: string): string {
  const cleaned = name.replace(/[^\w.-]+/g, "_").replace(/_+/g, "_");
  return cleaned || "video.mov";
}

function errorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code;
  if (code === "storage/canceled") return "Prijenos je otkazan.";
  if (code === "storage/unauthorized") return "Nemate dozvolu za prijenos ovog videa.";
  if (
    code === "storage/retry-limit-exceeded" ||
    code === "nn/stalled" ||
    code === "nn/network" ||
    code === "nn/server"
  ) {
    return "Prijenos je zastao. Provjerite internetsku vezu i pokušajte ponovno.";
  }
  if (code === "nn/read-failed") {
    return "Video se nije mogao pročitati s uređaja. Odaberite ga ponovno.";
  }
  if (code === "nn/size-mismatch") {
    return "Video se nije prenio u cijelosti. Pokušajte ponovno.";
  }
  const message = (error as { message?: string })?.message;
  return message && !message.startsWith("Firebase")
    ? message
    : "Prijenos videa nije uspio. Pokušajte ponovno.";
}

function codedError(code: string): Error & { code: string } {
  return Object.assign(new Error(code), { code });
}

async function run(id: string) {
  const entry = entries.get(id);
  if (!entry) return;
  const { options } = entry;
  runCleanup(entry);
  patch(id, { status: "uploading", progress: 0, error: undefined });
  options.onAttempt?.();

  let storagePath: string | undefined;
  let uploaded = false;
  entry.cancelRequested = false;
  try {
    if (entry.uploadedVideo) {
      await save(id, entry, entry.uploadedVideo);
      return;
    }
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Niste prijavljeni.");

    const invalid = await validateVideoFile(options.file);
    if (invalid) throw new Error(invalid);
    if (entry.cancelRequested) throw codedError("storage/canceled");

    storagePath = `${options.folder}/${uid}/${Date.now()}-${safeFileName(options.file.name)}`;
    const storageRef = ref(storage, storagePath);
    const contentType = options.file.type || "video/quicktime";
    const cacheControl = "public, max-age=31536000, immutable";

    // Stall detection. The timer is reset when the app returns to the
    // foreground so time spent suspended doesn't count as a stall.
    let lastProgressAt = Date.now();
    let stalled = false;
    const onVisible = () => {
      if (document.visibilityState === "visible") lastProgressAt = Date.now();
    };
    document.addEventListener("visibilitychange", onVisible);
    const stallTimer = setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastProgressAt > STALL_TIMEOUT_MS
      ) {
        stalled = true;
        entry.cancel?.();
      }
    }, 5_000);
    entry.cleanup.push(
      () => clearInterval(stallTimer),
      () => document.removeEventListener("visibilitychange", onVisible),
    );
    const reportProgress = (sent: number, total: number) => {
      lastProgressAt = Date.now();
      const pct = total ? Math.min(100, Math.floor((sent / total) * 100)) : 0;
      if (pct !== entries.get(id)?.job.progress) patch(id, { progress: pct });
    };

    let uploadedSize: number | undefined;
    const uploadStarted = performance.now();
    try {
      if (getLocalVideoPath(options.file)) {
        // Picked with the native gallery picker: upload from disk in pieces.
        const controller = new AbortController();
        entry.cancel = () => controller.abort();
        const result = await resumableUpload({
          storagePath,
          size: options.file.size,
          contentType,
          cacheControl,
          customMetadata: options.metadata,
          readChunk: (start, end) => readLocalVideoChunk(options.file, start, end),
          onProgress: (sent) => reportProgress(sent, options.file.size),
          signal: controller.signal,
        });
        uploadedSize = result.size;
      } else {
        const task: UploadTask = uploadBytesResumable(storageRef, options.file, {
          contentType,
          cacheControl,
          customMetadata: options.metadata,
        });
        entry.cancel = () => task.cancel();
        await new Promise<void>((resolve, reject) => {
          task.on(
            "state_changed",
            (snap) => reportProgress(snap.bytesTransferred, snap.totalBytes),
            (err) => reject(err),
            () => resolve(),
          );
        });
        uploadedSize = task.snapshot.metadata.size;
      }
    } catch (error) {
      throw stalled ? codedError("nn/stalled") : error;
    } finally {
      entry.cancel = undefined;
    }
    runCleanup(entry);
    uploaded = true;
    diag("video uploaded", {
      mb: Math.round(options.file.size / (1024 * 1024)),
      s: Math.round(since(uploadStarted) / 1000),
      fromDisk: !!getLocalVideoPath(options.file),
    });

    // Guard against empty/truncated uploads (the old "broken video" bug).
    if (!uploadedSize || uploadedSize !== options.file.size) {
      throw codedError("nn/size-mismatch");
    }

    const url = await getDownloadURL(storageRef);
    entry.uploadedVideo = { url, storagePath };
    await save(id, entry, entry.uploadedVideo);
  } catch (error) {
    console.error("Video upload failed:", error);
    runCleanup(entry);
    // A finished upload is kept when only saving failed: "Ponovi" saves it
    // again (and the message may in fact have been saved, pointing at it).
    if (uploaded && storagePath && !entry.uploadedVideo) {
      deleteObject(ref(storage, storagePath)).catch(() => { });
    }
    patch(id, { status: "error", error: errorMessage(error) });
    options.onFailed?.(error);
  }
}

/** Creates the message/post for an uploaded video (options.onUploaded). */
async function save(id: string, entry: JobEntry, video: UploadedVideo) {
  patch(id, { progress: 100, status: "processing" });
  saving.add(id);
  let docPath: string | undefined;
  try {
    docPath = await entry.options.onUploaded(video);
  } finally {
    saving.delete(id);
  }
  releaseLocalVideo(entry.options.file);
  if (docPath) {
    watchProcessing(id, docPath);
  } else {
    finish(id);
  }
}

function watchProcessing(id: string, docPath: string) {
  const entry = entries.get(id);
  if (!entry) return;
  const timeout = setTimeout(() => finish(id), PROCESSING_WATCH_TIMEOUT_MS);
  const unsubscribe = onSnapshot(
    doc(db, docPath),
    (snap) => {
      if (!snap.exists() || snap.data()?.videoProcessing !== true) finish(id);
    },
    () => finish(id),
  );
  entry.cleanup.push(() => clearTimeout(timeout), unsubscribe);
}

function finish(id: string) {
  const entry = entries.get(id);
  if (!entry || entry.job.status === "done") return;
  runCleanup(entry);
  patch(id, { status: "done", progress: 100 });
  setTimeout(() => dismissVideoUpload(id), DONE_DISMISS_MS);
}

/** Starts a background upload and returns its job id. */
export function startVideoUpload(options: StartVideoUploadOptions): string {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  entries.set(id, {
    job: {
      id,
      label: options.label,
      fileName: options.file.name,
      status: "uploading",
      progress: 0,
    },
    options,
    cleanup: [],
  });
  emit();
  void run(id);
  return id;
}

/** Tries a failed upload again; false if there's no such upload any more. */
export function retryVideoUpload(id: string): boolean {
  const entry = entries.get(id);
  if (!entry) return false;
  if (entry.job.status === "error") void run(id);
  return true;
}

export function cancelVideoUpload(id: string) {
  const entry = entries.get(id);
  if (entry?.job.status !== "uploading") return;
  // Still checking the video: stop before the upload starts.
  entry.cancelRequested = true;
  entry.cancel?.();
}

export function dismissVideoUpload(id: string) {
  const entry = entries.get(id);
  if (!entry) return;
  if (entry.job.status === "uploading") entry.cancel?.();
  runCleanup(entry);
  releaseLocalVideo(entry.options.file);
  entries.delete(id);
  emit();
}
